import { Component, lazy, Suspense, useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  CampaignDraft,
  RecruitmentCampaign,
  RecruitmentOptions,
  RecruitmentTransport,
} from '@rudi/shared/types/recruitment';
import { getRecruitmentPanels, loadRecruitmentPanels } from './recruitment-extensions';
import { Button } from './button';
import RecruitmentReporting from './recruitment-reporting';
import { Input } from './input';
import { Textarea } from './textarea';
import { RecruitmentMultisite } from './recruitment-multisite';

const emptyDraft: CampaignDraft = {
  name: '',
  siteId: '',
  studyIds: [],
  diseaseFocus: '',
  channels: ['meta'],
  recruitmentArea: '',
  country: 'DE',
  language: 'de',
  audience: 'adults_self',
  startsOn: null,
  endsOn: null,
  copy: '',
};
function statusOf(error: unknown): number | undefined {
  return (
    (error as { response?: { status?: number }; status?: number })?.response?.status ??
    (error as { status?: number })?.status
  );
}
function message(error: unknown): string {
  if (statusOf(error) === 409)
    return 'Eine neuere Version liegt vor. Ihre Eingaben bleiben erhalten. Öffnen Sie bei Bedarf den gespeicherten Stand.';
  if (statusOf(error) === 403)
    return 'Keine Berechtigung für diese Prüfstelle oder Studienauswahl.';
  if (statusOf(error) === 422) return 'Bitte prüfen Sie Ihre Angaben und den Zeitraum.';
  return 'Die Kampagne konnte nicht geladen oder gespeichert werden. Bitte erneut versuchen.';
}
class PanelBoundary extends Component<
  { children: ReactNode; version: number },
  { failed: boolean; version: number }
> {
  state = { failed: false, version: this.props.version };
  static getDerivedStateFromProps(props: { version: number }, state: { version: number }) {
    return props.version !== state.version ? { failed: false, version: props.version } : null;
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? (
      <p role="alert">Das Kampagnenmodul konnte nicht geladen werden.</p>
    ) : (
      this.props.children
    );
  }
}
const noPanels = {};
export function RecruitmentWorkspace({
  transport,
  panelModules = noPanels,
}: {
  transport: RecruitmentTransport;
  panelModules?: Record<string, () => Promise<{ register: () => void }>>;
}) {
  const siteControlId = useId();
  const copyControlId = useId();
  const [campaigns, setCampaigns] = useState<RecruitmentCampaign[]>([]);
  const [options, setOptions] = useState<RecruitmentOptions>({ actions: [], studies: [] });
  const [selected, setSelected] = useState<RecruitmentCampaign | null>(null);
  const [draft, setDraft] = useState<CampaignDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [reportOnly, setReportOnly] = useState(false);
  const [error, setError] = useState('');
  const [moduleError, setModuleError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(false);
  const [recoveryCopy, setRecoveryCopy] = useState<CampaignDraft | null>(null);
  const [registeredPanels, setRegisteredPanels] = useState(getRecruitmentPanels);
  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    setModuleError('');
    try {
      try {
        await loadRecruitmentPanels(panelModules);
      } catch {
        setModuleError('Das Kampagnenmodul konnte nicht geladen werden. Bitte erneut laden.');
      } finally {
        setRegisteredPanels(getRecruitmentPanels());
      }
      const [rows, choices] = await Promise.all([transport.list(), transport.options()]);
      setCampaigns(rows);
      setOptions(choices);
      setUnavailable(false);
      setReportOnly(false);
    } catch (err) {
      if (statusOf(err) === 403 && transport.funding) setReportOnly(true);
      else if ([404, 405].includes(statusOf(err) ?? 0)) setUnavailable(true);
      else setError(message(err));
    } finally {
      setLoading(false);
    }
  }, [transport, panelModules]);
  useEffect(() => {
    void reload();
  }, [reload]);
  const sites = new Map<string, string>();
  for (const study of options.studies)
    for (const id of study.management.recruitment_site_ids) {
      sites.set(id, study.centers?.find(site => site.catalog_site_id === id)?.name ?? id);
    }
  const shared = selected
    ? selected.authoringResponsibility === 'ivelion'
    : options.authoringResponsibility === 'ivelion';
  const editable = selected
    ? selected.actions.includes('save')
    : options.actions.includes('create');
  const panels = useMemo(
    () =>
      registeredPanels
        .filter(panel => !transport.panelIds || transport.panelIds.includes(panel.id))
        .map(panel => ({
          ...panel,
          Component: lazy(panel.load),
        })),
    [registeredPanels, transport.panelIds]
  );
  async function open(id: string, preserveRecovery = false) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await transport.get(id);
      setSelected(saved);
      setDraft(saved);
      setConflict(false);
      if (!preserveRecovery) setRecoveryCopy(null);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!draft || busy) return;
    if (
      shared &&
      (!draft.participants?.length || draft.participants.some(p => !p.studyIds.length))
    ) {
      setError('Wählen Sie für jede teilnehmende Prüfstelle mindestens eine verknüpfte Studie.');
      return;
    }
    if (!draft.studyIds.length) {
      setError('Wählen Sie mindestens eine verknüpfte Studie.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = selected
        ? await transport.save(selected.id, draft, selected.version)
        : await transport.create(draft);
      setSelected(saved);
      setDraft(saved);
      setCampaigns(rows => [saved, ...rows.filter(row => row.id !== saved.id)]);
      setNotice(`Gespeichert. Version ${saved.version}.`);
      setConflict(false);
      setRecoveryCopy(null);
    } catch (err) {
      setError(message(err));
      setConflict(statusOf(err) === 409);
    } finally {
      setBusy(false);
    }
  }
  if (loading)
    return (
      <p role="status" className="p-6">
        Kampagnen werden geladen…
      </p>
    );
  if (reportOnly)
    return (
      <div className="overflow-auto p-4 md:p-6">
        <RecruitmentReporting transport={transport} />
      </div>
    );
  if (unavailable)
    return <p className="p-6">Digital marketing ist auf diesem Server noch nicht verfügbar.</p>;
  return (
    <section className="space-y-5 overflow-auto p-4 md:p-6" aria-label="Digital marketing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Digital marketing</h2>
          <p className="text-sm text-muted-foreground">
            Studien in Deutschland. Deutsche Inhalte für Erwachsene, die selbst eine Studie suchen.
          </p>
          <p className="text-sm text-muted-foreground">
            Erstellen Sie zuerst einen Kampagnenentwurf. Texte und Bilder können Sie ohne
            verbundenes Werbekonto vorbereiten.
          </p>
        </div>
        {options.actions.includes('create') && (
          <Button
            disabled={busy}
            onClick={() => {
              setSelected(null);
              setDraft({
                ...emptyDraft,
                ...(options.authoringResponsibility === 'ivelion'
                  ? { participants: [], commissioningKind: 'sponsor' as const }
                  : {}),
              });
              setRecoveryCopy(null);
              setError('');
              setNotice('');
              setConflict(false);
            }}
          >
            Neue Kampagne
          </Button>
        )}
      </div>
      {(error || moduleError) && (
        <div role="alert" className="space-y-2 rounded-3xl border border-destructive p-4">
          {error && <p>{error}</p>}
          {moduleError && <p>{moduleError}</p>}
          {conflict && selected ? (
            <Button
              variant="outline"
              onClick={() => {
                setRecoveryCopy(draft);
                void open(selected.id, true);
              }}
            >
              Gespeicherten Stand öffnen
            </Button>
          ) : (
            <Button variant="outline" onClick={() => void reload()}>
              Erneut laden
            </Button>
          )}
        </div>
      )}
      {notice && <p role="status">{notice}</p>}
      {!error && !sites.size && !shared && (
        <p role="status">
          Für die ausgewählte Person oder Ihr Team sind keine Studien mit zugeordneter Prüfstelle
          verfügbar. Wählen Sie eine andere Person oder lassen Sie die Studienzuordnung prüfen.
        </p>
      )}
      {recoveryCopy && (
        <Button
          variant="outline"
          onClick={() => {
            setDraft(recoveryCopy);
            setRecoveryCopy(null);
          }}
        >
          Eigene Eingaben wiederherstellen
        </Button>
      )}
      <div className="grid gap-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <nav aria-label="Kampagnenentwürfe" className="space-y-2">
          {!campaigns.length && !error && (
            <p className="text-sm text-muted-foreground">Noch keine Kampagnenentwürfe.</p>
          )}
          {campaigns.map(campaign => (
            <button
              type="button"
              key={campaign.id}
              disabled={busy}
              aria-current={selected?.id === campaign.id ? 'true' : undefined}
              className="w-full break-words rounded-3xl border p-3 text-left hover:bg-muted focus-visible:outline focus-visible:outline-2"
              onClick={() => void open(campaign.id)}
            >
              {campaign.name}
            </button>
          ))}
        </nav>
        {draft && (
          <div className="min-w-0 space-y-4">
            <form
              className="min-w-0 space-y-4 rounded-3xl border p-4"
              onSubmit={event => {
                event.preventDefault();
                void save();
              }}
            >
              <fieldset disabled={busy || !editable} className="min-w-0 space-y-4">
                <label className="block space-y-1">
                  Kampagnenname
                  <Input
                    required
                    maxLength={200}
                    value={draft.name}
                    onChange={e => setDraft({ ...draft, name: e.target.value })}
                  />
                </label>
                {shared ? (
                  <RecruitmentMultisite
                    draft={draft}
                    options={options}
                    onChange={setDraft}
                    editable={editable}
                  />
                ) : (
                  <>
                    <div className="space-y-1">
                      <label htmlFor={siteControlId}>Prüfstelle</label>
                      <select
                        id={siteControlId}
                        required
                        onInvalid={event =>
                          event.currentTarget.setCustomValidity('Bitte wählen Sie eine Prüfstelle.')
                        }
                        disabled={!!selected}
                        className="block w-full rounded-3xl border bg-background p-2"
                        value={draft.siteId}
                        onChange={e => {
                          e.target.setCustomValidity('');
                          setDraft({ ...draft, siteId: e.target.value, studyIds: [] });
                        }}
                      >
                        <option value="">Prüfstelle wählen</option>
                        {[...sites].map(([id, name]) => (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        ))}
                        {selected && !sites.has(selected.siteId) && (
                          <option value={selected.siteId}>{selected.siteId}</option>
                        )}
                      </select>
                    </div>
                    <fieldset className="space-y-2">
                      <legend className="mb-2 font-medium">Verknüpfte Studien</legend>
                      {options.studies
                        .filter(study =>
                          study.management.recruitment_site_ids.includes(draft.siteId)
                        )
                        .map(study => (
                          <label
                            key={study.catalog_id}
                            className="flex items-start gap-2 break-words rounded-3xl border p-3"
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={draft.studyIds.includes(study.catalog_id)}
                              onChange={e =>
                                setDraft({
                                  ...draft,
                                  studyIds: e.target.checked
                                    ? [...draft.studyIds, study.catalog_id]
                                    : draft.studyIds.filter(id => id !== study.catalog_id),
                                })
                              }
                            />
                            <span className="min-w-0">{study.title}</span>
                          </label>
                        ))}
                      {draft.studyIds
                        .filter(id => !options.studies.some(study => study.catalog_id === id))
                        .map(id => (
                          <p key={id}>{id}</p>
                        ))}
                    </fieldset>
                  </>
                )}
                <label className="block space-y-1">
                  Erkrankung
                  <Input
                    required
                    maxLength={500}
                    value={draft.diseaseFocus}
                    onChange={e => setDraft({ ...draft, diseaseFocus: e.target.value })}
                  />
                </label>
                <label className="block space-y-1">
                  Rekrutierungsgebiet
                  <Input
                    required
                    maxLength={500}
                    value={draft.recruitmentArea}
                    onChange={e => setDraft({ ...draft, recruitmentArea: e.target.value })}
                  />
                </label>
                <fieldset className="flex flex-wrap gap-4">
                  <legend>Kanäle</legend>
                  {(['meta', 'google_search'] as const).map(channel => (
                    <label className="flex gap-2" key={channel}>
                      <input
                        type="checkbox"
                        checked={draft.channels.includes(channel)}
                        onChange={e =>
                          setDraft({
                            ...draft,
                            channels: e.target.checked
                              ? [...draft.channels, channel]
                              : draft.channels.filter(value => value !== channel),
                          })
                        }
                      />
                      {channel === 'meta' ? 'Facebook und Instagram' : 'Google Search'}
                    </label>
                  ))}
                </fieldset>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label>
                    Beginn
                    <Input
                      type="date"
                      value={draft.startsOn ?? ''}
                      onChange={e => setDraft({ ...draft, startsOn: e.target.value || null })}
                    />
                  </label>
                  <label>
                    Ende
                    <Input
                      type="date"
                      min={draft.startsOn ?? undefined}
                      value={draft.endsOn ?? ''}
                      onChange={e => setDraft({ ...draft, endsOn: e.target.value || null })}
                    />
                  </label>
                </div>
                <div className="space-y-1">
                  <label htmlFor={copyControlId}>Anzeigentext</label>
                  <Textarea
                    id={copyControlId}
                    rows={6}
                    maxLength={10000}
                    value={draft.copy}
                    onChange={e => setDraft({ ...draft, copy: e.target.value })}
                  />
                </div>
              </fieldset>
              {editable && (
                <Button type="submit" disabled={!draft.channels.length} aria-disabled={busy}>
                  {busy ? 'Wird gespeichert…' : 'Entwurf speichern'}
                </Button>
              )}
              <p className="text-sm text-muted-foreground">
                Entwurf. Bezahlte Auslieferung ist deaktiviert.
              </p>
              {selected && (
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt>Auftraggebende Organisation</dt>
                    <dd className="break-all">
                      {selected.commissioningOrganizationName ??
                        selected.commissioningOrganizationId}
                    </dd>
                  </div>
                  <div>
                    <dt>Bearbeitung</dt>
                    <dd>
                      {selected.authoringResponsibility === 'ivelion' ? 'Ivelion' : 'Prüfstelle'}
                    </dd>
                  </div>
                  <div>
                    <dt>Verantwortliche Person</dt>
                    <dd className="break-all">{selected.authoredBy}</dd>
                  </div>
                  <div>
                    <dt>Version</dt>
                    <dd>{selected.version}</dd>
                  </div>
                </dl>
              )}
            </form>
            {selected &&
              panels
                .filter(panel => selected.actions.includes(panel.action))
                .map(({ id, Component: Panel }) => (
                  <PanelBoundary key={`${id}:${selected.id}`} version={selected.version}>
                    <Suspense fallback={<p role="status">Modul wird geladen…</p>}>
                      <Panel
                        draftDirty={JSON.stringify(draft) !== JSON.stringify(selected)}
                        campaign={selected}
                        transport={transport}
                        onChange={campaign => {
                          setSelected(campaign);
                          setDraft(current => {
                            if (!current) return campaign;
                            const edits = Object.fromEntries(
                              Object.entries(current).filter(
                                ([key, value]) =>
                                  JSON.stringify(value) !==
                                  JSON.stringify(selected[key as keyof RecruitmentCampaign])
                              )
                            );
                            return { ...campaign, ...edits };
                          });
                        }}
                      />
                    </Suspense>
                  </PanelBoundary>
                ))}
          </div>
        )}
      </div>
      <RecruitmentReporting transport={transport} />
    </section>
  );
}

export type {
  RecruitmentCampaign,
  RecruitmentOptions,
  RecruitmentTransport,
} from '@rudi/shared/types/recruitment';

export type { RecruitmentDeliveryState, RecruitmentEntry } from '@rudi/shared/types/recruitment';
